import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Ledger, theme } from '@sg/garnish';

import { useLedgerItems } from '../../hooks/useLedgerItems';
import { type OrderingLedger } from '../../machine/ordering-machine.types';

export const BagPriceSummary = (props: BagPriceSummaryProps) => {
  const {
    ledger,
    amountEligibleForFlex,
    tip,
    maxTipAmount,
    availableCredit,
    discountInfo,
    shouldUseCredit,
    shouldUseFlex,
  } = props;

  const ledgerItems = useLedgerItems({
    ledger,
    tip,
    maxTipAmount,
    discountInfo,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
  });

  return (
    <Ledger.Container style={styles.container}>
      <View style={styles.content}>
        <BodyText sizeMatch={['14']} bold style={styles.title}>
          <FormattedMessage {...messages.title} />
        </BodyText>

        {ledgerItems.filter(isValidLedgerItem).map((item) => (
          <Ledger.Row key={item.label} sizeMatch={['14']} numberOfLines={undefined} {...item} />
        ))}
      </View>
    </Ledger.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidLedgerItem(item: ReturnType<typeof useLedgerItems>[number]) {
  return Boolean(item && item.label && item.amount);
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly title: NoMessageValues;
  readonly split: NoMessageValues;
  readonly paymentMethod: { readonly card: MessageValue };
}>({
  title: {
    defaultMessage: 'Price Summary',
    description: 'Bag | Price Summary | Title',
  },
  split: {
    defaultMessage: 'Payment split between two cards',
    description: 'Bag | Price Summary | Split',
  },
  paymentMethod: {
    defaultMessage: 'Default - {card}',
    description: 'Bag | Price Summary | Payment method',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.OPACITY.OATMEAL.DARK,
    borderRadius: theme.radius.large,
    paddingTop: theme.spacing['2'],
    paddingBottom: theme.spacing['2'],
  },
  content: {
    paddingVertical: theme.spacing['4'],
    paddingHorizontal: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  paymentSplit: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.OPACITY.LINEN.DARK,
  },
  splitTitle: {
    textAlign: 'center',
    paddingBottom: theme.spacing['4'],
  },
  title: {
    paddingBottom: theme.spacing['4'],
    textAlign: 'center',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagPriceSummaryProps = {
  ledger?: OrderingLedger;
  amountEligibleForFlex: number | null;
  discountInfo?: string;
  tip: number;
  maxTipAmount: number;
  availableCredit: number;
  shouldUseCredit: boolean;
  shouldUseFlex: boolean;
};
